import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Popover,
  Paper,
  Button,
  Typography,
  IconButton,
  Grid,
  alpha,
  Chip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  EventNote as EventNoteIcon
} from '@mui/icons-material';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfDay, getDaysInMonth, getDay, isSameDay, isWithinInterval } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

/**
 * Composant DateRangePicker ergonomique avec calendrier élégant
 * Permet de sélectionner une plage de dates (startDate → endDate)
 */
const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onDateRangeChange, 
  label = 'Plage de dates',
  placeholder = 'jj/mm/aaaa → jj/mm/aaaa',
  size = 'small'
}) => {
  const { t, i18n } = useTranslation('financial');
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [tempStartDate, setTempStartDate] = useState(startDate ? new Date(startDate) : null);
  const [tempEndDate, setTempEndDate] = useState(endDate ? new Date(endDate) : null);

  // Locale date-fns
  const dateLocale = i18n?.language?.startsWith('fr') ? fr
    : i18n?.language?.startsWith('ar') ? ar
    : enUS;

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDayClick = (day) => {
    if (selectingStart) {
      setTempStartDate(day);
      setSelectingStart(false);
    } else {
      if (day < tempStartDate) {
        setTempEndDate(tempStartDate);
        setTempStartDate(day);
      } else {
        setTempEndDate(day);
      }
    }
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onDateRangeChange(
        format(tempStartDate, 'yyyy-MM-dd'),
        format(tempEndDate, 'yyyy-MM-dd')
      );
      handleClose();
    }
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setSelectingStart(true);
    onDateRangeChange('', '');
    handleClose();
  };

  const handleReset = () => {
    setTempStartDate(startDate ? new Date(startDate) : null);
    setTempEndDate(endDate ? new Date(endDate) : null);
    setSelectingStart(true);
  };

  // Formater l'affichage
  const displayText = startDate && endDate
    ? `${format(new Date(startDate), 'dd/MM/yyyy')} → ${format(new Date(endDate), 'dd/MM/yyyy')}`
    : placeholder;

  // Générer les jours du mois
  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getDay(startOfMonth(currentMonth));
  const days = [];
  
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
  }

  return (
    <Box>
      <TextField
        fullWidth
        size={size}
        label={label}
        value={displayText}
        onClick={handleClick}
        readOnly
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <EventNoteIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
          ),
          endAdornment: startDate ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              sx={{ mr: -1 }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : null
        }}
        sx={{ 
          cursor: 'pointer',
          '& .MuiOutlinedInput-root': {
            fontWeight: 500,
            fontSize: '0.95rem',
            letterSpacing: '0.3px'
          }
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper sx={{ 
          p: 2.5, 
          width: 300,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          borderRadius: '12px'
        }}>
          {/* En-tête du calendrier */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <IconButton 
              size="small" 
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              sx={{ '&:hover': { bgcolor: alpha('#1976d2', 0.08) } }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: '0.5px' }}>
              {format(currentMonth, 'MMMM yyyy', { locale: dateLocale })}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              sx={{ '&:hover': { bgcolor: alpha('#1976d2', 0.08) } }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Info sélection */}
          <Box sx={{ mb: 2, p: 1.2, bgcolor: alpha('#1976d2', 0.06), borderRadius: '8px', border: `1px solid ${alpha('#1976d2', 0.15)}` }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>                       
                    {tempStartDate && tempEndDate && (
                    <Chip
                        label={`${format(tempStartDate, 'dd/MM')} → ${format(tempEndDate, 'dd/MM')}`}
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ mt: 0.75, fontWeight: 600, fontSize: '0.8rem' }}
                    />
                    )}
             </Typography>
          </Box>

          {/* En-têtes jours */}
          <Grid container spacing={0.3} sx={{ mb: 1 }}>
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(day => (
              <Grid item xs={12 / 7} key={day}>
                <Typography
                  variant="caption"
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 800, 
                    color: 'text.disabled',
                    fontSize: '0.7rem',
                    display: 'block'
                  }}
                >
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Grille des jours */}
          <Grid container spacing={0.3} sx={{ mb: 2 }}>
            {days.map((day, idx) => {
              const isInRange = day && tempStartDate && tempEndDate && isWithinInterval(day, { start: tempStartDate, end: tempEndDate });
              const isStart = day && tempStartDate && isSameDay(day, tempStartDate);
              const isEnd = day && tempEndDate && isSameDay(day, tempEndDate);
              const isToday = day && isSameDay(day, new Date());

              return (
                <Grid item xs={12 / 7} key={idx}>
                  <Button
                    onClick={() => day && handleDayClick(day)}
                    disabled={!day}
                    size="small"
                    fullWidth
                    sx={{
                      py: 0.5,
                      px: 0,
                      fontSize: '0.8rem',
                      minWidth: 0,
                      aspectRatio: '1',
                      backgroundColor:
                        isStart || isEnd ? 'primary.main' : isInRange ? alpha('#1976d2', 0.15) : 'transparent',
                      color: isStart || isEnd ? 'white' : isToday && !(isStart || isEnd) ? 'primary.main' : 'text.primary',
                      fontWeight: isStart || isEnd ? 700 : isToday ? 600 : 400,
                      border: isToday && !(isStart || isEnd) ? `2px solid ${alpha('#1976d2', 0.4)}` : 'none',
                      '&:hover': !day ? {} : {
                        backgroundColor: isStart || isEnd ? 'primary.dark' : alpha('#1976d2', 0.25),
                        transform: 'scale(1.05)'
                      },
                      cursor: day ? 'pointer' : 'default',
                      borderRadius: isStart ? '8px 4px 4px 8px' : isEnd ? '4px 8px 8px 4px' : '8px',
                      transition: 'all 0.15s ease',
                      '&:disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    {day ? day.getDate() : ''}
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          {/* Boutons d'action */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button 
              size="small" 
              variant="text"
              onClick={handleReset}
              sx={{ textTransform: 'none', fontSize: '0.85rem' }}
            >
              🔄
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              disabled={!tempStartDate || !tempEndDate}
              sx={{ textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 }}
            >
              ✔️
            </Button>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default DateRangePicker;
